import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronUp, Maximize2, PackageCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { escalationLabel, needsInputHeading, suggestedDateNote } from "@/lib/clydeEscalations";
import { productSearchScore } from "@/lib/productSearch";
import { activeAliases } from "@shared/productAliases";
import { fingerprintResolvedLines } from "@shared/productMatch";
import type { AgentEscalation, ProductMatchPayload } from "@shared/schema";

/**
 * Clyde's "needs your input" items, in the conversation (Jon, 2026-10-21): each
 * open escalation is a strip under the message it came from — collapsed to one
 * line, expandable in place, openable as a lightbox — and a held order's strip
 * carries the resolution card. The page places the strips (lib/clydeEscalations.ts).
 */

// Minimal product shape the resolution card needs (GET /products/active — list
// context, imageData excluded server-side per the payload rules).
export interface CatalogProduct {
  id: number;
  name: string;
  subheading: string | null;
  aliases: unknown;
}

// Client-side mirror of the server's MAX_LINE_QTY ceiling (server/services/
// agentLineItems.ts) — the server re-validates; this just gives instant feedback.
const MAX_LINE_QTY = 500;

interface ResolutionLineState {
  productId: number | null;
  qty: string; // raw input text; validated to integer 1..MAX_LINE_QTY on save
  skip: boolean;
  // Phase C: where the mapping is remembered. Per-buyer is the default — the
  // exemplar corpus shows account-specific meanings are the norm ("carrot" =
  // 50lb Jumbo only for The Snug). "All buyers" writes an org-wide alias.
  scope: "account" | "org";
}

interface SuggestedDate {
  date: string | null;
  source: "held" | "thread" | "next_delivery" | null;
  quote?: string;
}

function lineQtyValid(qty: string): boolean {
  const n = Number(qty);
  return Number.isInteger(n) && n >= 1 && n <= MAX_LINE_QTY;
}

function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * Alias-aware product picker — same cmdk pattern as the order-slip add-item box,
 * with a search that forgives a typo ("beats" finds Beets — lib/productSearch.ts):
 * the admin types what the buyer wrote.
 */
function ProductCombobox({
  products,
  value,
  onSelect,
  testId,
}: {
  products: CatalogProduct[];
  value: number | null;
  onSelect: (productId: number) => void;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value != null ? products.find((p) => p.id === value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full sm:w-64 justify-between h-9 font-normal"
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.name : "Pick a product…"}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command filter={productSearchScore}>
          <CommandInput placeholder="Search products…" className="h-9" />
          <CommandEmpty>No products found.</CommandEmpty>
          <CommandList className="max-h-[260px]">
            <CommandGroup>
              {[...products]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => (
                  <CommandItem
                    key={p.id}
                    // cmdk filters on `value` — fold in the subheading and active
                    // aliases so "usa" or "romas" surfaces the right SKU. The id
                    // keeps two products with the same text apart.
                    value={[p.name, p.subheading ?? "", ...activeAliases(p.aliases), `#${p.id}`].join(" ")}
                    onSelect={() => {
                      onSelect(p.id);
                      setOpen(false);
                    }}
                  >
                    <span className="truncate">
                      {p.name}
                      {p.subheading ? <span className="text-muted-foreground"> — {p.subheading}</span> : null}
                    </span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Phase B resolution card for a `product_match` escalation — the learning loop.
 * Each held unresolved line gets a product mapping + quantity (or an explicit
 * skip); Save materializes the held order through the buyer-path machinery,
 * stores each mapping as a learned+active alias, and texts the buyer the normal
 * confirmation (skipped lines named). Lines with `matchedName` are RECOGNIZED
 * products that tripped the quantity ceiling — rendered as a quantity question,
 * not an unknown term.
 *
 * The date never blocks the save: it opens prefilled (the held date, a day the
 * buyer named in the thread since, or the next delivery day — server
 * suggestFulfillmentDate) and says where it came from. A save also closes the
 * conversation's question about the same item, and the toast names it.
 */
export function ProductMatchResolutionCard({
  escalation,
  products,
  onResolved,
}: {
  escalation: AgentEscalation;
  products: CatalogProduct[];
  onResolved: () => void;
}) {
  const { toast } = useToast();
  const rawPayload = escalation.payload as ProductMatchPayload | null;
  // Shape-guard the json payload (defensive vs hand-edited/legacy rows): any
  // malformed entry means "no usable card" rather than a white-screen — the
  // server applies the same rule and points at the thread.
  const payload = useMemo(() => {
    if (!rawPayload || !Array.isArray(rawPayload.unresolvedLines) || !Array.isArray(rawPayload.resolvedLines)) return null;
    const linesOk = rawPayload.unresolvedLines.every(
      (h) => h && typeof h.term === "string" && (h.qty === null || typeof h.qty === "number"),
    );
    const resolvedOk = rawPayload.resolvedLines.every(
      (r) => r && typeof r.name === "string" && typeof r.qty === "number",
    );
    return linesOk && resolvedOk ? rawPayload : null;
  }, [rawPayload]);
  const heldLines = useMemo(
    () => (payload ? payload.unresolvedLines : []),
    [payload],
  );

  const initialLines = useMemo<ResolutionLineState[]>(
    () =>
      heldLines.map((h) => {
        const matched = h.matchedName ? products.find((p) => p.name === h.matchedName) : undefined;
        const qtyOk = typeof h.qty === "number" && Number.isInteger(h.qty) && h.qty >= 1 && h.qty <= MAX_LINE_QTY;
        return {
          productId: matched?.id ?? null,
          qty: qtyOk ? String(h.qty) : "",
          skip: false,
          scope: "account" as const,
        };
      }),
    [heldLines, products],
  );
  const [lines, setLines] = useState<ResolutionLineState[]>(initialLines);

  const suggestedQuery = useQuery<SuggestedDate>({
    queryKey: ["/api/agent/escalations", escalation.id, "suggested-date"],
    enabled: !!payload,
  });
  const suggested = suggestedQuery.data;
  // The admin's pick, or null while they have not touched the date (then the
  // suggestion shows, and an empty one lets the server fill it in).
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const date = pickedDate ?? payload?.requestedDate ?? suggested?.date ?? "";
  const dateNote = pickedDate == null ? suggestedDateNote(payload?.requestedDate ? { source: "held" } : suggested) : null;

  // A buyer revision refreshes the open row's payload (last text wins) — when the
  // refetched escalation arrives, re-seed the form so the admin never edits a
  // stale list. Keyed on payload CONTENT, not updatedAt: a failed save reopens
  // the row (bumping updatedAt without changing the payload), and wiping the
  // admin's mappings on exactly the "Try again" errors would be brutal.
  const seedKey = `${escalation.id}:${JSON.stringify(payload?.unresolvedLines ?? [])}:${payload?.requestedDate ?? ""}:${JSON.stringify(payload?.resolvedLines ?? [])}`;
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededRef.current !== seedKey) {
      seededRef.current = seedKey;
      setLines(initialLines);
      setPickedDate(null);
    }
  }, [seedKey, initialLines]);

  const queryClient = useQueryClient();
  const resolveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/agent/escalations/${escalation.id}/resolve-order`, {
        // Empty = the server's own suggestion (the same one shown here).
        fulfillmentDate: date,
        lines: heldLines.map((h, i) => ({
          term: h.term,
          rawQty: h.qty ?? null,
          productId: lines[i]?.skip ? null : lines[i]?.productId ?? null,
          qty: lines[i]?.skip ? null : Number(lines[i]?.qty),
          skip: lines[i]?.skip === true,
          scope: lines[i]?.scope ?? "account",
        })),
        // Stale-card echo: the server compares these against the CLAIMED row so a
        // buyer revision (even date-only or resolved-qty-only) can't slip through.
        heldRequestedDate: payload?.requestedDate ?? null,
        // Shared implementation with the server — a client/server drift here
        // would 409 every save (found by review: the two sides had diverged).
        resolvedFingerprint: fingerprintResolvedLines(payload?.resolvedLines ?? []),
      });
      return res.json() as Promise<{
        orderId: number;
        warnings: string[];
        alsoResolved?: Array<{ id: number; type: string; detail: string | null }>;
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/escalations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations", escalation.conversationId] });
      // Mappings became live aliases — product search + Settings both change.
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/active?includeHiddenCategories=1"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/alias-suggestions"] });
      // One toast (the app shows one at a time, so a second would hide the first):
      // the outcome, any warning, and — never closed silently — each other item
      // this answered.
      const alsoClosed = (data.alsoResolved ?? []).map(
        (e) => `Also closed — ${escalationLabel(e.type).toLowerCase()}: ${e.detail ?? "(no detail)"}`,
      );
      const lines = data.warnings.length > 0 ? [...data.warnings, ...alsoClosed] : [`Order #${data.orderId} created — confirmation texted to the buyer.`, ...alsoClosed];
      toast({
        variant: data.warnings.length > 0 ? "destructive" : "default",
        description: <span className="whitespace-pre-line" data-testid="resolve-toast">{lines.join("\n")}</span>,
      });
      onResolved();
    },
    onError: (e: unknown) => {
      let msg = (e instanceof Error ? e.message : String(e)) || "Failed to resolve.";
      const m = msg.match(/^\d+:\s*([\s\S]*)$/);
      if (m) {
        try {
          msg = JSON.parse(m[1]).message || m[1];
        } catch {
          msg = m[1];
        }
      }
      toast({ variant: "destructive", description: msg });
      // A stale-card 409 means the buyer revised the held order — pull the fresh
      // payload so the form re-seeds with the new list.
      queryClient.invalidateQueries({ queryKey: ["/api/agent/escalations"] });
    },
  });

  const resolvedLines = payload?.resolvedLines ?? [];
  const allSkipped = lines.length > 0 && lines.every((l) => l.skip) && resolvedLines.length === 0;
  // The date is not part of it: an empty one is the server's suggestion.
  const formValid =
    heldLines.length === lines.length &&
    lines.every((l) => l.skip || (l.productId != null && lineQtyValid(l.qty))) &&
    !allSkipped;

  if (!payload || heldLines.length === 0) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        No held order details on this escalation — handle it from the thread.
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="product-match-card">
      {resolvedLines.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Already matched:{" "}
          <span className="text-gray-700">{resolvedLines.map((r) => `${r.qty} ${r.name}`).join(", ")}</span>
        </p>
      )}
      <div className="space-y-2">
        {heldLines.map((h, i) => {
          const line = lines[i] ?? { productId: null, qty: "", skip: false, scope: "account" as const };
          const setLine = (patch: Partial<ResolutionLineState>) =>
            setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
          return (
            <div key={`${h.term}:${i}`} className="rounded-md border bg-white p-2" data-testid="product-match-line">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="font-medium">“{h.term}”</span>
                  {h.qty != null && <span className="text-muted-foreground"> ×{h.qty}</span>}
                  {h.matchedName && (
                    <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
                      recognized as {h.matchedName} — confirm the quantity
                    </span>
                  )}
                </div>
                <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                  <Checkbox
                    checked={line.skip}
                    onCheckedChange={(v) => setLine({ skip: v === true })}
                    data-testid={`skip-term-${i}`}
                  />
                  Skip — not a product
                </label>
              </div>
              {!line.skip && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ProductCombobox
                    products={products}
                    value={line.productId}
                    onSelect={(productId) => setLine({ productId })}
                    testId={`pick-product-${i}`}
                  />
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_LINE_QTY}
                    step={1}
                    value={line.qty}
                    onChange={(e) => setLine({ qty: e.target.value })}
                    placeholder="Qty"
                    className="h-9 w-24"
                    data-testid={`qty-term-${i}`}
                  />
                  <Select value={line.scope} onValueChange={(v) => setLine({ scope: v === "org" ? "org" : "account" })}>
                    <SelectTrigger className="h-9 w-full sm:w-48" data-testid={`scope-term-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account">Remember: this buyer only</SelectItem>
                      <SelectItem value="org">Remember: all buyers</SelectItem>
                    </SelectContent>
                  </Select>
                  {line.qty !== "" && !lineQtyValid(line.qty) && (
                    <span className="text-xs text-red-600">Whole number 1–{MAX_LINE_QTY} (bigger orders go in via the dashboard)</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="grid gap-1">
          <Label htmlFor={`resolve-date-${escalation.id}`} className="text-xs">
            Fulfillment date
          </Label>
          <Input
            id={`resolve-date-${escalation.id}`}
            type="date"
            value={date}
            onChange={(e) => setPickedDate(e.target.value)}
            className="h-9 w-44"
            data-testid="resolve-date"
          />
          {dateNote && (
            <span className="max-w-[16rem] truncate text-[11px] text-muted-foreground" title={dateNote} data-testid="resolve-date-note">
              {dateNote}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {allSkipped && (
            <span className="text-xs text-muted-foreground">Everything skipped — use Dismiss instead of creating an empty order.</span>
          )}
          <Button
            size="sm"
            className="gap-1"
            disabled={!formValid || resolveMutation.isPending}
            onClick={() => resolveMutation.mutate()}
            data-testid="resolve-save"
          >
            <PackageCheck className="h-4 w-4" />
            {resolveMutation.isPending ? "Creating order…" : "Create order & text buyer"}
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Each mapping is remembered (for this buyer, or everyone), so this question won't come back.
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * One open item under its message: a single amber line (what, when, the start of
 * the detail), expandable in place, or opened as a lightbox. A held order's
 * strip holds the resolution card; the card renders in one place at a time.
 */
export function EscalationStrip({
  escalation,
  products,
  productsLoading,
  onDismiss,
  dismissing,
  defaultOpen = false,
  alignRight = false,
}: {
  escalation: AgentEscalation;
  products: CatalogProduct[];
  productsLoading: boolean;
  onDismiss: () => void;
  dismissing: boolean;
  defaultOpen?: boolean;
  alignRight?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const [lightbox, setLightbox] = useState(false);
  const isProductMatch = escalation.type === "product_match";
  const label = escalationLabel(escalation.type);

  const body = (inLightbox: boolean) => (
    <>
      {escalation.detail && (
        <p className={`whitespace-pre-wrap text-sm text-gray-800 ${inLightbox ? "" : "mt-2"}`}>{escalation.detail}</p>
      )}
      {isProductMatch &&
        (productsLoading ? (
          <p className="mt-2 text-xs text-muted-foreground">Loading catalog…</p>
        ) : (
          <ProductMatchResolutionCard
            escalation={escalation}
            products={products}
            onResolved={() => {
              setExpanded(false);
              setLightbox(false);
            }}
          />
        ))}
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="ghost" disabled={dismissing} onClick={onDismiss} data-testid="escalation-dismiss">
          Dismiss
        </Button>
      </div>
    </>
  );

  return (
    <div
      // Collapsed it is one line under the bubble; open, it takes the thread's width.
      className={`mt-1 w-full rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-left ${expanded ? "" : "max-w-[85%]"} ${alignRight ? "self-end" : "self-start"}`}
      data-testid="escalation-strip"
      data-escalation-id={escalation.id}
      data-escalation-type={escalation.type}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={expanded}
          data-testid="escalation-strip-toggle"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
          <span className="shrink-0 text-xs font-medium text-amber-900">{needsInputHeading(escalation)} · {label}</span>
          {!expanded && escalation.detail && (
            <span className="min-w-0 truncate text-xs text-amber-900/70">{escalation.detail}</span>
          )}
          <span className="ml-auto shrink-0 text-[10px] text-amber-800/70">{relativeTime(escalation.createdAt)}</span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-amber-700" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-amber-700" />}
        </button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0 text-amber-800"
          onClick={() => {
            setExpanded(false);
            setLightbox(true);
          }}
          title="Open"
          aria-label="Open in a larger view"
          data-testid="escalation-strip-open"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {expanded && !lightbox && body(false)}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="max-w-2xl" data-testid="escalation-lightbox">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              {label}
            </DialogTitle>
            <DialogDescription>Flagged {relativeTime(escalation.createdAt)}.</DialogDescription>
          </DialogHeader>
          {lightbox && body(true)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
